/**
 * Types du module Parois
 * 
 * Gestion des panneaux verticaux et parois latérales de la tonnelle
 */

// ===== TYPES DE BASE =====
export interface Position3D {
  x: number;
  y: number;
  z: number;
}

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

// ===== ÉNUMÉRATIONS =====

/**
 * Types de matériaux pour les parois
 */
export enum TypeMateriauParoi {
  TUBE_CARRE = 'tube_carre',
  FER_A_BETON = 'fer_a_beton',
  PANNEAU_BOIS = 'panneau_bois',
  PANNEAU_COMPOSITE = 'panneau_composite',
  VERRE_TREMPE = 'verre_trempe',
  POLYCARBONATE = 'polycarbonate',
  TOILE_TENDUE = 'toile_tendue'
}

/**
 * Types de fixation des parois
 */
export enum TypeFixationParoi {
  PERMANENT = 'permanent',
  AMOVIBLE = 'amovible',
  COULISSANT = 'coulissant',
  PLIABLE = 'pliable'
}

/**
 * Motifs pour les parois ajourées
 */
export enum TypeMotifParoi {
  PLEIN = 'plein',
  AJOURE_SIMPLE = 'ajoure_simple',
  AJOURE_GEOMETRIQUE = 'ajoure_geometrique',
  CLAUSTRAS = 'claustras',
  PERSONNALISE = 'personnalise'
}

// ===== INTERFACES PAROIS =====

/**
 * Paramètres d'une paroi individuelle
 */
export interface ParametresParoi {
  id: string;
  nom: string;
  materiau: TypeMateriauParoi;
  fixation: TypeFixationParoi;
  motif: TypeMotifParoi;
  hauteur: number; // en mètres
  largeur: number; // en mètres
  epaisseur: number; // en mètres
  couleur: string;
  transparence: number; // 0-1
  decalageSol: number; // décalage par rapport au sol en mètres
  pasEntreMontants?: number; // 1 = chaque intervalle, 2 = un sur deux, etc.
  actif: boolean;
}

/**
 * Paramètres globaux du module Parois
 */
export interface ParametresParois {
  actif: boolean;
  parois: ParametresParoi[];
  hauteurDefaut: number;
  epaisseurDefaut: number;
  materiauDefaut: TypeMateriauParoi;
  couleurDefaut: string;
  fixationDefaut: TypeFixationParoi;
  motifDefaut: TypeMotifParoi;
  transparenceDefaut: number;
}

/**
 * Structure géométrique d'une paroi générée
 */
export interface PanneauParoi {
  id: string;
  nom: string;
  geometrie: {
    coins: Point3D[];
    centre: Point3D;
    normale: Point3D;
    surface: number;
  };
  parametres: ParametresParoi;
  vertices: number[];
  indices: number[];
  materiaux: {
    diffuse: string;
    ambient: string;
    specular: string;
    shininess: number;
    transparency: number;
  };
}

/**
 * Structure complète des parois
 */
export interface StructureParois {
  id: string;
  timestamp: number;
  parametres: ParametresParois;
  panneaux: PanneauParoi[];
  statistiques: {
    nombrePanneaux: number;
    surfaceTotale: number;
    materiauPrincipal: TypeMateriauParoi;
    poidesEstime: number; // en kg
  };
}

/**
 * Données de la structure de base nécessaires pour les parois
 */
export interface DonneesStructureBasePourParois {
  forme: string;
  largeur: number;
  longueur: number;
  hauteur: number;
  nombreMontants: number;
  positionsMontants: Position3D[];
  materiau: string;
  epaisseur: number;
  couleur: string;
  poutreHaute?: {
    points: Point3D[];
  };
  poutreBasse?: {
    points: Point3D[];
  };
  centre: Position3D;
}

/**
 * État du module Parois
 */
export interface ParoisState {
  parametres: ParametresParois;
  structure?: StructureParois;
  isGenerating: boolean;
  error?: string;
  editeurActif: boolean;
  paroiSelectionnee?: string;
}

/**
 * Motif personnalisé pour parois ajourées
 */
export interface MotifPersonnaliseParoi {
  id: string;
  nom: string;
  description: string;
  pattern: {
    largeur: number;
    hauteur: number;
    elements: {
      type: 'rectangle' | 'circle' | 'polygon';
      position: Point3D;
      dimensions: { largeur: number; hauteur: number };
      rotation?: number;
    }[];
  };
  apercu: string; // URL de l'image d'aperçu
}

// ===== PARAMÈTRES PAR DÉFAUT =====

export const PARAMETRES_PAROIS_DEFAUT: ParametresParois = {
  actif: false,
  parois: [],
  hauteurDefaut: 2.0,
  epaisseurDefaut: 0.02,
  materiauDefaut: TypeMateriauParoi.PANNEAU_COMPOSITE,
  couleurDefaut: '#8B7355',
  fixationDefaut: TypeFixationParoi.PERMANENT,
  motifDefaut: TypeMotifParoi.PLEIN,
  transparenceDefaut: 0.0
};

export const COULEURS_MATERIAUX_PAROIS: Record<TypeMateriauParoi, string> = {
  [TypeMateriauParoi.TUBE_CARRE]: '#8B4513',
  [TypeMateriauParoi.FER_A_BETON]: '#696969',
  [TypeMateriauParoi.PANNEAU_BOIS]: '#8B7355',
  [TypeMateriauParoi.PANNEAU_COMPOSITE]: '#A0A0A0',
  [TypeMateriauParoi.VERRE_TREMPE]: '#E6F3FF',
  [TypeMateriauParoi.POLYCARBONATE]: '#F0F8FF',
  [TypeMateriauParoi.TOILE_TENDUE]: '#F5F5DC'
};